import React, { useContext, useEffect, useMemo, useState } from 'react'
import API from '../../../../services/API'
//@MUI
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
} from '@mui/material'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import moment from 'moment'
import TripleCheck from '../../../../components/Switch/TripleCheck'
import VDICheckForm from './VDICheckForm'
import StaffSelector from '../../../events/StaffSelector'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import SmallModal from '../../../../components/Modals/SmallModal'
import Lottie from 'lottie-react'
import successLottie from '../../../../assets/lottie/formSubmission2.json'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

const VehicleCleanForm = () => {
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState({})
  const [cleanStartDate, setCleanStartDate] = useState(
    moment().subtract(1, 'hour')
  )
  const [cleanEndDate, setCleanEndDate] = useState(moment())
  const [cleanType, setCleanType] = useState('Interim')
  const [deconReason, setDeconReason] = useState('')
  const [atpTestCompleted, setAtpTestCompleted] = useState('')
  const [reasonAtpNotCompleted, setReasonAtpNotCompleted] = useState('')
  const [atpTestType, setAtpTestType] = useState('')
  const [atpPassed, setAtpPassed] = useState('')
  const [vdiData, setVdiData] = useState({})
  const [operative, setOperative] = useState(null)
  const [lastDeepClean, setLastDeepClean] = useState(null)
  const [delayedDeepCleanExemption, setDelayedDeepCleanExemption] =
    useState(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  const query = useQuery()
  const vehicleID = query.get('vehicle')

  useEffect(() => {
    API.get('/vehicleList')
      .then((res) => {
        setVehicleOptions(res.data.filter((x) => x.vehicleType))
      })
      .catch((e) => console.log(e))
  }, [])

  useEffect(() => {
    if (vehicleID && vehicleOptions.length > 0) {
      console.log('vehicleOptions', vehicleOptions)
      console.log('vehicleID', vehicleID)
      setSelectedVehicle(vehicleOptions.find((x) => x._id === vehicleID))
    }
  }, [vehicleID, vehicleOptions])

  useEffect(() => {
    if (Object.keys(selectedVehicle).length > 0 && !!cleanType) {
      API.post('/vehiclecleanoptions', {
        vehicleType: selectedVehicle.vehicleType,
        cleanType: cleanType,
      }).then((res) => {
        setVdiData(res.data)
      })
    }
  }, [selectedVehicle, cleanType])

  useEffect(() => {
    if (Object.keys(selectedVehicle).length > 0) {
      API.post('/deepcleantiming', {
        vehicle: selectedVehicle._id,
        startDate: cleanStartDate,
      }).then((res) => setLastDeepClean(res.data))
    }
  }, [selectedVehicle])

  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  const somethingNotComplete = Object.keys(vdiData).some((x) =>
    vdiData[x].some((y) => y.value !== false && y.value !== true)
  )

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const handleSubmit = (e) => {
    e.preventDefault()
    API.post('/vehicleclean', {
      vehicle: selectedVehicle._id,
      start: cleanStartDate,
      end: cleanEndDate,
      operative,
      cleanType,
      atpTestCompleted: !!atpTestCompleted,
      atpTestType,
      atpTestPassed: !!atpPassed,
      atpNotCompletedReason: reasonAtpNotCompleted,
      deconReason,
      cleanResults: Object.keys(vdiData).reduce((accumulator, item) => {
        if (!accumulator[item]) accumulator[item] = []
        vdiData[item].forEach(({ title, value }) => {
          if (value !== undefined)
            accumulator[item].push({
              title,
              value,
            })
        })
        return accumulator
      }, {}),
    }).then((res) => {
      if (res.status === 200) {
        setShowSuccessMessage(true)
        setToast({
          message: 'Vehicle Clean Recorded',
        })
      } else {
        setToast({
          message: 'Unable to save Vehicle Clean. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Stack gap={1} component="form" onSubmit={handleSubmit}>
      <Stack gap={1} component={Paper} padding={1}>
        <Typography variant="title2">Record Vehicle Clean</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={4}>
            <Autocomplete
              options={vehicleOptions ?? []}
              disableClearable
              getOptionLabel={(opt) => opt?.callSign ?? ''}
              onChange={(e, value) => setSelectedVehicle(value)}
              value={
                Object.keys(selectedVehicle).length > 0 ? selectedVehicle : {}
              }
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  required
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <AnDateTimePicker
              fullWidth
              date={cleanStartDate}
              onChange={(newDate) => setCleanStartDate(newDate)}
              label="Start"
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <AnDateTimePicker
              fullWidth
              date={cleanEndDate}
              onChange={(newDate) => setCleanEndDate(newDate)}
              label="End"
            />
          </Grid>
          <Grid item xs={12}>
            {lastDeepClean && (
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography variant="title3">Last Deep Clean:</Typography>
                <Typography>
                  {moment(lastDeepClean).format('Do MMM YYYY')}
                </Typography>
                {moment(cleanStartDate).diff(lastDeepClean, 'weeks') > 12 ? (
                  <Stack direction="row" gap={2} alignItems="center">
                    <Chip
                      color="error"
                      sx={{ borderRadius: 1 }}
                      size="small"
                      label={`Overdue: Was due on ${moment(lastDeepClean)
                        .add(orgSettings?.deepCleanFrequency ?? 0, 'weeks')
                        .format('Do MMM YYYY')}`}
                    />
                    <FormControlLabel
                      label="Exempt from KPI (Vehicle Unavailable / Defective)"
                      control={
                        <Switch
                          checked={delayedDeepCleanExemption}
                          color="success"
                          onChange={(e) =>
                            setDelayedDeepCleanExemption(e.target.checked)
                          }
                        />
                      }
                    />
                  </Stack>
                ) : (
                  <Chip
                    color="success"
                    sx={{ borderRadius: 1 }}
                    size="small"
                    label={`Due ${moment(lastDeepClean)
                      .add(orgSettings?.deepCleanFrequency ?? 0, 'weeks')
                      .format('Do MMM YYYY')}`}
                  />
                )}
              </Stack>
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            <StaffSelector
              value={operative}
              onChange={(e, value) => {
                console.log('value', value)
                setOperative(value)
              }}
              label="Operative"
              required
              noPhoto
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <InputLabel>Type of Clean</InputLabel>
              <Select
                value={cleanType}
                onChange={(e) => setCleanType(e.target.value)}
                label="Type of Clean"
              >
                <MenuItem value="Interim">Interim</MenuItem>
                <MenuItem value="Deep">Deep (Scheduled)</MenuItem>
                <MenuItem value="Decontamination">Decontamination</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {cleanType === 'Decontamination' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                value={deconReason}
                required={cleanType === 'Decontamination'}
                onChange={(e) => setDeconReason(e.target.value)}
                label="Reason for Decontamination"
              />
            </Grid>
          )}
          {cleanType !== 'Interim' && (
            <>
              <Grid item xs={12} lg={4}>
                <FormControl fullWidth>
                  <InputLabel>ATP Test Completed</InputLabel>
                  <Select
                    value={atpTestCompleted}
                    required={cleanType !== 'Interim'}
                    onChange={(e) => setAtpTestCompleted(e.target.value)}
                    label="ATP Test Completed"
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {atpTestCompleted === false ? (
                <Grid item xs={12} lg={8}>
                  <TextField
                    fullWidth
                    value={reasonAtpNotCompleted}
                    required={atpTestCompleted === false}
                    onChange={(e) => setReasonAtpNotCompleted(e.target.value)}
                    label="Reason ATP Not Completed"
                    autoComplete="off"
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type of Test</InputLabel>
                      <Select
                        value={atpTestType}
                        onChange={(e) => setAtpTestType(e.target.value)}
                        label="Type of Test"
                        required={atpTestCompleted === true}
                      >
                        <MenuItem value="Rapid Result">Rapid Result</MenuItem>
                        <MenuItem value="Luminometer">Luminometer</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <FormControl fullWidth>
                      <InputLabel>ATP Result</InputLabel>
                      <Select
                        value={atpPassed}
                        required={atpTestCompleted === true}
                        label="ATP Result"
                        onChange={(e) => setAtpPassed(e.target.value)}
                      >
                        <MenuItem value={true}>Pass</MenuItem>
                        <MenuItem value={false}>Fail</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Stack>

      <VDICheckForm
        isVehicleCleans
        data={vdiData}
        setData={setVdiData}
        selectedVehicleType={selectedVehicle?.vehicleType}
        selectedCleanType={cleanType}
      />
      <Paper padding={1}>
        {somethingNotComplete && (
          <Alert severity="info">
            All items needs to be marked as complete or incomplete
          </Alert>
        )}
        <Button
          fullWidth
          color="success"
          variant="contained"
          type="submit"
          disabled={somethingNotComplete}
        >
          Submit
        </Button>
      </Paper>
      <SmallModal open={showSuccessMessage}>
        <Stack>
          <Typography variant="title2" fontSize="1em" textAlign="center">
            Vehicle Clean Recorded
          </Typography>
          <Lottie animationData={successLottie} loop={false} />
          <Button
            onClick={() => navigate('/')}
            variant="contained"
            size="small"
            color="anBlue"
          >
            Return Home
          </Button>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default VehicleCleanForm
